import { useIdle, useUpdateEffect } from '@lbcamden/lib-react';
import { useEffect } from 'react';

import { isLocal } from '~/client/config/appConfig';
import { useCurrentUser, useUserStore } from '~/client/hooks/storeHooks';
import { useDetectPageHidden } from '~/client/hooks/useDetectPageHidden';
import { useAppStore } from '~/client/store/app/appStore';
import { useTinybaseStore } from '~/client/store/tinybase/tinybaseStore';

const maxIdleTimeAppFocused = 60e3; // 60s
const maxIdleTimeAppHidden = 15e3; // 15s
const maxIdleTimeLocalDev = 2.88e7; // 8 hours
const maxIdleTimeOnCamdenNetwork = 3e5; // 5 mins

const forceIdleTimerReset = () => dispatchEvent(new Event('mousemove'));

const getMaxIdleTime = (pageHidden: boolean, isOnCamdenNetwork?: boolean) => {
  if (isLocal) {
    return maxIdleTimeLocalDev;
  }
  if (isOnCamdenNetwork) {
    return maxIdleTimeOnCamdenNetwork;
  }
  return pageHidden ? maxIdleTimeAppHidden : maxIdleTimeAppFocused;
};

/**
 * Updates the store to require the user to re-authenticate on the client
 * if they are idle for a predetermined amount of time
 */
export const useDetectNeedsClientAuth = () => {
  const currentUser = useCurrentUser();

  const needsClientAuth = useAppStore((state) => state.needsClientAuth);
  const isOnCamdenNetwork = useAppStore((state) => state.isOnCamdenNetwork);
  const setNeedsClientAuth = useAppStore((state) => state.setNeedsClientAuth);
  const userStoreCrypto = useTinybaseStore((state) => state.userStoreCrypto);
  const userStore = useUserStore();

  const pageHidden = useDetectPageHidden();
  const isIdle = useIdle(getMaxIdleTime(pageHidden, isOnCamdenNetwork));

  const shouldPrompt = !!currentUser && (isIdle || !userStoreCrypto);

  useEffect(() => {
    if (shouldPrompt) {
      userStoreCrypto?.clearKey();
      void userStore?.persister?.stopAutoSave();
      setNeedsClientAuth(true);
    }
  }, [isIdle]);

  useUpdateEffect(() => {
    if (!needsClientAuth) {
      void userStore?.persister?.startAutoSave();
      forceIdleTimerReset();
    }
  }, [needsClientAuth]);
};
